<template>
    <div>
        <Loading page v-if="loading" />

        <div class="mb-3 audit-button-container" v-if="!loading">
            <div class="float-left">
                <button type="button" 
                    class="btn btn-primary"
                    @click="showFilterModal()">
                    Filtro
                </button>
            </div>

            <slot name="auditToggle" />
        </div>

        <FilterInformationCmp 
            v-if="!loading"
            :filter-submitted="auditMuvenApiFilterSubmitted"
            :filter-properties="auditMuvenApiFilterProperties" />

        <div class="modal" tabindex="-1" role="dialog" id="muven-audit-instance-additional-data-modal">
            <div class="modal-dialog modal-lg" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Dados trafegados</h5>
                        <p class="modal-subtitle">Enviado em {{audit.date | dateTimeFilter}}</p>
                    </div>

                    <div class="modal-body">
                        <div>                        
                            <tree-view 
                                :deep="4"
                                :highlight-mouseover-node="true"
                                :show-length="true"
                                :data="audit" />
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button 
                            type="button"
                            class="btn btn-outline-secondary"
                            @click="downloadAuditHandler(audit)">
                            Baixar
                        </button>

                        <button type="button" 
                            class="btn btn-outline-secondary" 
                            @click="closeAdditionalDataModal()">                                    
                            Fechar
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="modal" tabindex="-1" role="dialog" id="muven-audit-instance-list-filter-modal">
            <div class="modal-dialog" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Filtro de auditoria</h5>
                    </div>
                    <div class="modal-body">
                        <div>
                            <div class="row">
                                <div class="col-12 col-md-6">
                                    <div class="form-group">
                                        <label>Data inicial</label>
                                        <v-date-picker 
                                            :input-props="{'class': 'form-control'}" 
                                            popover-visibility="focus"
                                            v-model="auditMuvenApiFilter.date.start" />
                                    </div>
                                </div>

                                <div class="col-12 col-md-6">
                                    <div class="form-group">
                                        <label>Data final</label>
                                        <v-date-picker 
                                            :input-props="{'class': 'form-control'}" 
                                            popover-visibility="focus"
                                            v-model="auditMuvenApiFilter.date.end" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" 
                            class="btn btn-primary" 
                            @click="getAuditListWithFilter(auditMuvenApiFilter)"
                            :disabled="loadingFilter">                                    
                                Filtrar
                                <Loading v-show="loadingFilter" />
                        </button>

                        <button type="button" 
                            class="btn btn-outline-secondary" 
                            @click="clearFilter()">Restaurar</button>

                        <button type="button" 
                            class="btn btn-outline-secondary" 
                            @click="closeFilterModal()">Fechar</button>
                    </div>
                </div>
            </div>
        </div>

        <NothingFound v-if="nothingToShow" />

        <div v-if="!loading && !nothingToShow">
            <div class="table-responsive">
                <table class="table table-hover table-sm table-striped">
                    <thead>
                        <tr>
                            <th>Entidade</th>
                            <th>Chamada</th>
                            <th class="datetime-col">Data e hora</th>
                            <th class="datetime-col">Resposta</th>
                            <th></th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr v-for="(audit, index) in auditList" :key="index">
                            <td>{{audit.getEntity()}}</td>
                            <td>{{audit.getRequestCall()}}</td>
                            <td>
                                <span class="muven-td-icon">
                                    <fa icon="check-circle" 
                                        v-if="audit.isStatusSuccess()"
                                        class="text-success" />

                                    <fa icon="exclamation-triangle"
                                        v-if="audit.isStatusError()"
                                        class="text-danger" />

                                    <fa icon="exclamation-triangle"
                                        v-if="audit.isStatusWarning()"
                                        class="text-warning" />

                                </span>

                                <span>{{audit.getStatus()}}</span>
                            </td>
                            <td>{{audit.date | dateTimeFilter}}</td>
                            <td class="text-center muven-td-with-buttons">
                                <button type="button" 
                                    class="btn btn-default text-muted" 
                                    data-toggle="dropdown" >
                                    <fa icon="ellipsis-v" v-show="!audit.loadingListAction" />
                                    <fa icon="sync-alt" spin v-show="audit.loadingListAction" />
                                </button>

                                <div class="dropdown-menu dropdown-menu-right muven-action-menu">
                                    <span class="dropdown-item"
                                        @click="showAdditionalDataModal(audit)">
                                        <fa icon="eye" fixed-width class="muven-action-icon" />
                                        <span class="muven-action-label">Dados trafegados</span>
                                    </span>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <ListCount :amount="auditList.length" />
            </div>

            <div v-if="showPaginationButton" class="text-center">
                <button class="btn btn-outline-secondary"
                    @click="getMoreAuditList()"
                    :disabled="loadingNextPage">
                    <span v-show="!loadingNextPage">Mais resultados</span>
                    <fa v-show="loadingNextPage" icon="sync-alt" spin/>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import {cloneDeep, uniqBy} from 'lodash'
import {dateTimeFilter} from '@/utils/filters/DateTimeFilter'
import {AuditMuvenApiModel} from '@/audit/models/AuditMuvenApiModel'
import {PaginationModel} from '@/utils/models/PaginationModel'
import auditService from '@/audit/services/AuditService'
import {AuditMuvenApiListFilterModel} from '@/audit/models/AuditMuvenApiListFilterModel'
import modalService from '@/utils/services/ModalService'
import FilterInformationCmp from '@/utils/components/FilterInformationCmp'
import GetEntityUrl from '@/utils/mixins/GetEntityUrl'
import VueJsonPretty from 'vue-json-pretty';
import DownloadJson from '@/utils/mixins/DownloadJson';

const ID_FILTER_MODAL = '#muven-audit-instance-list-filter-modal'
const ID_ADDITIONAL_DATA_MODAL = '#muven-audit-instance-additional-data-modal'

export default {
    name: 'AuditMuvenApiListView',
    data() {
        return {
            loading: true,
            loadingNextPage: false,
            loadingFilter: false,
            showPaginationButton: true,
            auditList: [],
            audit: new AuditMuvenApiModel(),
            pagination: new PaginationModel(),
            auditMuvenApiFilter: new AuditMuvenApiListFilterModel(),
            auditMuvenApiFilterSubmitted: new AuditMuvenApiListFilterModel(),
            auditMuvenApiFilterProperties: [
                {
                    type: 'daterange',
                    label: 'Período',
                    property: 'date',
                },                
            ]
        }
    },
    components: {
        FilterInformationCmp,
        'tree-view': VueJsonPretty,
    },
    filters: {
        dateTimeFilter,        
    },
    mixins: [
        GetEntityUrl,
        DownloadJson,
    ],
    methods: {
        goto(audit) {
            this.$router.push(`/auditorias/${audit._id}`);
        },
        downloadAuditHandler(audit) {
            this.downloadJSON(audit, `Auditoria ${audit.getEntity()} - ${audit.date.toISOString()}.json`)
        },
        async getMoreAuditList() {
            try {
                this.loadingNextPage = true
                const newAuditList = await auditService.getMuvenApiInfo(this.pagination.actual(), this.auditMuvenApiFilter)
                this.auditList.push(...newAuditList)

                if (!newAuditList.length) {
                    this.showPaginationButton = false
                }

                this.pagination.next()
            } catch(e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.loadingNextPage = false
        },
        async getAuditListWithFilter(filterList) {
            this.showPaginationButton = true
            this.auditMuvenApiFilterSubmitted = cloneDeep(filterList)
            this.pagination.reset()
            this.loadingFilter = true
            try {
                this.auditList = await auditService.getMuvenApiInfo(this.pagination.actual(), filterList)
                this.pagination.next()                
                this.closeFilterModal()
            } catch(e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.loadingFilter = false
        },
        async showFilterModal() {
            await modalService.show(ID_FILTER_MODAL)
        },
        async closeFilterModal() {
            await modalService.hide(ID_FILTER_MODAL)
        },
        clearFilter() {
            this.auditMuvenApiFilter = new AuditMuvenApiListFilterModel()
        },
        async showAdditionalDataModal(audit) {
            audit.loadingListAction = true

            try {
                this.audit = await auditService.getMuvenApiInfoById(audit._id)
                await modalService.show(ID_ADDITIONAL_DATA_MODAL)
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message,
                })   
            }

            audit.loadingListAction = false
        }, 
        async closeAdditionalDataModal() {
            await modalService.hide(ID_ADDITIONAL_DATA_MODAL)
        },
    },
    async mounted() {
        try {
            const querystring = this.$route.query
            this.auditMuvenApiFilter = new AuditMuvenApiListFilterModel(querystring)
            this.auditList = await auditService.getMuvenApiInfo(this.pagination.actual(), this.auditMuvenApiFilter)
            this.pagination.next()

            if (querystring) {                
                this.auditMuvenApiFilterSubmitted = cloneDeep(this.auditMuvenApiFilter)
            }
        } catch(e) {
            this.$floatingAlert.error({
                message: e.message,
            })
        }

        this.loading = false
    },
    computed: {
        nothingToShow() {
            return !this.loading && !this.auditList.length
        },     
        detailUrl() {
            return (audit) => {
                return `/auditorias/${audit._id}`
            }
        },
    },
}
</script>

<style lang="scss" scoped>
#muven-audit-instance-additional-data-modal {
    .modal-body {
        max-height: calc(80vh - 100px);
        overflow: auto;
    }
}

.audit-button-container {
    overflow: hidden;
}
</style>